import { Chip, Typography } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  CheckRounded,
  ClearRounded,
  HourglassEmptyRounded,
  ThumbUpOutlined
} from '@material-ui/icons';
import clsx from 'clsx';
import moment from 'moment';
import BotIcon from '../icon/BotIcon';
import { ApprovalTypeLabel } from '../pages/ProjectDocumentWorkflow/ProjectDocumentWorkflowApproval';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(0.5, 0)
    },
    successColor: {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText
    },
    errorColor: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.success.contrastText
    },
    whiteColor: {
      color: theme.palette.success.contrastText
    },
    disabledColor: {
      backgroundColor: theme.palette.text.disabled
    },
    paddingLR: {
      padding: theme.spacing(0, 0.5),
      marginRight: theme.spacing(1)
    },
    paddingLR2: {
      padding: theme.spacing(0, 0.5, 0, 1),
      marginRight: theme.spacing(1)
    }
  })
);

export default function ApproveChip({
  approvalTypeId,
  submittedDate,
  dueDate,
  isAutoApproval = false,
  disabled = false
}: {
  approvalTypeId: number | null;
  submittedDate: Date;
  dueDate?: Date;
  isAutoApproval?: boolean;
  disabled?: boolean;
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const isApproved = approvalTypeId === ApprovalTypeLabel.Approve;
  const isRejected = approvalTypeId === ApprovalTypeLabel.Reject;
  const isAcknowledge = approvalTypeId === ApprovalTypeLabel.Acknowledge;

  let chip = (
    <>
      <Chip
        className={clsx(classes.paddingLR, {
          [classes.disabledColor]: disabled
        })}
        label={t('approveChip.pending') as string}
        color="primary"
        size="small"
        icon={<HourglassEmptyRounded />}
      />
      {dueDate && (
        <Typography
          variant="caption"
          color={disabled ? 'textSecondary' : 'textPrimary'}
        >
{t('approveChip.dueDate')}: {moment(dueDate).format('ll')}
        </Typography>
      )}
    </>
  );
  if (isAutoApproval) {
    chip = (
      <>
        <Chip
          className={classes.paddingLR2}
          label={t('approveChip.autoApproved') as string}
          variant="outlined"
          size="small"
          icon={<BotIcon />}
        />
        <Typography variant="caption">
          {moment(submittedDate).format('LLL')}
        </Typography>
      </>
    );
  } else if (isApproved || isRejected) {
    chip = (
      <>
        <Chip
          className={clsx(classes.paddingLR, classes.successColor, {
            [classes.errorColor]: isRejected
          })}
          label={isApproved ? t('approveChip.approved') as string : t('approveChip.rejected') as string}
          size="small"
          icon={
            isApproved ? (
              <CheckRounded className={classes.whiteColor} />
            ) : (
              <ClearRounded className={classes.whiteColor} />
            )
          }
        />
        <Typography variant="caption">
          {moment(submittedDate).format('LLL')}
        </Typography>
      </>
    );
  } else if (isAcknowledge) {
    chip = (
      <>
        <Chip
          className={classes.paddingLR2}
          label={t('approveChip.acknowledged') as string}
          variant="outlined"
          size="small"
          icon={<ThumbUpOutlined />}
        />
        <Typography variant="caption">
          {moment(submittedDate).format('LLL')}
        </Typography>
      </>
    );
  }

  return <div className={classes.root}>{chip}</div>;
}
